import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';

import AppBadge from 'components/App/AppBadge';
import ColGroup from 'components/ColGroup';

import INITIAL_VALUE from 'constants/initialValue';
import { PROMOTION_STATUS } from 'constants/promotion';
import cn from 'utils/classnames';
import { convertIsoDateToString } from 'utils';

import styles from './DataTableManagerPromotion.module.scss';

const EDITABLE_STATUSES = [
  PROMOTION_STATUS.DRAFT,
  PROMOTION_STATUS.PENDING,
];

const DELETABLE_STATUSES = [
  PROMOTION_STATUS.DRAFT,
  PROMOTION_STATUS.PENDING,
  PROMOTION_STATUS.GOING,
];

const STATUS_DISPLAY = {
  [PROMOTION_STATUS.DRAFT]: {
    label: 'Chưa nhập nhật',
    color: 'warning',
  },
  [PROMOTION_STATUS.PENDING]: {
    label: 'Đã cập nhật',
    color: 'caution',
  },
  [PROMOTION_STATUS.GOING]: {
    label: 'Đang diễn ra',
    color: 'success',
  },
  [PROMOTION_STATUS.COMPLETED]: {
    label: 'Đã kết thúc',
    color: 'neutral',
  },
};

const HEADS = [
  'Tên',
  'Thời gian ưu đãi',
  'Số lượng đối tác tham gia',
  'Trạng thái',
  'Thao tác',
];

const COL_WIDTHS = [2.4, 1.8, 1.6, 1.2, 1.2];
const COL_WIDTH_SUM = COL_WIDTHS.reduce((sum, width) => sum + width, 0);

const formatPromotionPeriod = (startAt, endAt) => {
  const start = convertIsoDateToString(startAt);
  const end = convertIsoDateToString(endAt);

  if (!start && !end) return '-';
  if (!start || !end) return start || end;

  return `${start} - ${end}`;
};

const formatPartnerCount = (count) => {
  if (count === undefined || count === null) return '-';
  return new Intl.NumberFormat('vi-VN').format(count);
};

const getPromotionPeriod = (promotion) => ({
  startAt: promotion.bookingDate?.from || promotion.startAt,
  endAt: promotion.bookingDate?.to || promotion.endAt,
});

const getPartnerCount = (promotion) => (
  promotion.promotionCount ?? promotion.partnerCount
);

function DataTableManagerPromotion(props) {
  const { promotions, onEdit, onDelete } = props;

  const renderStatus = useCallback((status) => {
    const display = STATUS_DISPLAY[status];

    if (!display) return '-';

    return (
      <AppBadge color={display.color} className="Badge">
        {display.label}
      </AppBadge>
    );
  }, []);

  const renderActions = useCallback(
    (promotion) => {
      const canEdit = EDITABLE_STATUSES.includes(promotion.status);
      const canDelete = DELETABLE_STATUSES.includes(promotion.status);

      if (!canEdit && !canDelete) {
        return null;
      }

      return (
        <div className={styles.Actions}>
          {canEdit && (
            <button
              type="button"
              className="button-action button-link"
              onClick={() => onEdit?.(promotion)}
            >
              Sửa
            </button>
          )}

          {canDelete && (
            <button
              type="button"
              className="button-action button-destructive"
              onClick={() => onDelete?.(promotion)}
            >
              Xoá
            </button>
          )}
        </div>
      );
    },
    [onDelete, onEdit],
  );

  return (
    <div
      className={styles.Component}
      style={{ '--manager-promotion-table-cols': COL_WIDTH_SUM }}
    >
      {promotions.length === 0 ? (
        <div className="NoData">Chưa có dữ liệu</div>
      ) : (
        <Table className="Table">
          <ColGroup
            list={COL_WIDTHS}
            containerWidth={COL_WIDTH_SUM}
            paddingX={0}
          />

          <thead className="Table__Header">
            <tr className="Table__Header__Row">
              {HEADS.map((head) => (
                <th key={head} className="Table__Header__Row__Content">
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="Table__Body">
            {promotions.map((promotion) => {
              const { startAt, endAt } = getPromotionPeriod(promotion);

              return (
                <tr className="Table__Body__Row" key={promotion._id}>
                  <td className="Table__Body__Row__Content">
                    <div className="PromotionName">{promotion.name || '-'}</div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    <div className="DateCell">
                      {formatPromotionPeriod(startAt, endAt)}
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    <div className="NumberCell">
                      {formatPartnerCount(getPartnerCount(promotion))}
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    {renderStatus(promotion.status)}
                  </td>

                  <td className={cn('Table__Body__Row__Content', styles.ActionCell)}>
                    {renderActions(promotion)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

DataTableManagerPromotion.propTypes = {
  promotions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string,
      bookingDate: PropTypes.shape({
        from: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.instanceOf(Date),
        ]),
        to: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.instanceOf(Date),
        ]),
      }),
      startAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      endAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      promotionCount: PropTypes.number,
      partnerCount: PropTypes.number,
      status: PropTypes.oneOf(Object.values(PROMOTION_STATUS)),
    }),
  ),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

DataTableManagerPromotion.defaultProps = {
  promotions: [],
  onEdit: INITIAL_VALUE.FUNCTION,
  onDelete: INITIAL_VALUE.FUNCTION,
};

export default memo(DataTableManagerPromotion);
